import { Component, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { firstErrorMessage } from '../../forms/formly-errors';
import { FieldShellComponent } from '../../forms/field-shell.component';

const BASE =
  'w-full bg-white border rounded-[16px] px-4 py-3 font-base text-[15px] text-black placeholder:text-black/35 transition-colors focus:outline-none focus:ring-2';
const OK = 'border-black/10 focus:border-mty-primary focus:ring-mty-primary/15';
const ERR = 'border-mty-danger focus:border-mty-danger focus:ring-mty-danger/15';

@Component({
  selector: 'app-formly-mat-number',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, FormlyModule, FieldShellComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-field-shell
      [label]="props.label || ''"
      [for]="id"
      [required]="!!props.required"
      [hint]="props['hint'] || ''"
      [errorMessage]="isInvalid() ? errorMessage() : ''">
      <div class="relative">
        <input
          [id]="id"
          type="number"
          [min]="props['min'] ?? null"
          [max]="props['max'] ?? null"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [required]="!!props.required"
          [class]="inputClass()"
          [attr.aria-invalid]="isInvalid() ? 'true' : null"
          [attr.data-field-id]="field.id" />
        @if (props['suffix']) {
          <span class="absolute right-3 top-1/2 -translate-y-1/2 px-2.5 py-0.5 rounded-full bg-mty-primary/10 text-mty-primary font-alternate text-xs font-medium pointer-events-none">
            {{ props['suffix'] | translate }}
          </span>
        }
      </div>
    </app-field-shell>
  `,
})
export class MatNumberFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.formControl.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  inputClass(): string {
    const suffixPad = this.props['suffix'] ? ' pr-20' : '';
    return `${BASE}${suffixPad} ${this.isInvalid() ? ERR : OK}`;
  }

  errorMessage(): string {
    return firstErrorMessage(this.formControl);
  }
}
